import { createApp } from 'vue'
import OSProduct from '~/js/models/OSProduct'
import FProductCard from '~/js/components/productCard'
import CategoryListContent from '~/js/components/categoryListContent'
import ProductsSearchFilters from '~/js/components/productsSearchFilters'
import { isCollectionPage } from '~/js/helpers/page-helper'
import {
  searchProducts,
  defaultAggregations,
} from '~/js/helpers/searchProducts'
import { isDesktop } from '~/js/templates/index/helpers'

const ID_PRODUCTS_SEARCH = 'js-productsSearch'
const CLASS_STICKY_WRAPPER = 'is-products-search-sticky-enabled'

document.addEventListener('DOMContentLoaded', async () => {
  const elementProductsSearch = document.getElementById(ID_PRODUCTS_SEARCH)
  if (!elementProductsSearch) return

  const { normalizedParams, didNormalize } =
    normalizeLegacyProductsSearchParams(window.location.search)

  // 旧形式クエリを変換した場合のみ、履歴を増やさずURLを正規化する。
  if (didNormalize) {
    const normalizedQuery = normalizedParams.toString()
    const nextUrl = `${window.location.pathname}${
      normalizedQuery ? `?${normalizedQuery}` : ''
    }${window.location.hash}`
    window.history.replaceState({}, '', nextUrl)
  }

  const urlParams = normalizedParams
  const keyword = urlParams.get('q') || ''
  const sortBy = urlParams.get('sort_by') || 'relevance'
  const collectionId = elementProductsSearch.dataset.collectionId || undefined
  const onlyInStock = urlParams.get('in_stock') === '1'
  const productTypes = urlParams.getAll('product_type')
  const tasteTags = urlParams.getAll('taste_tag')
  const colorTags = urlParams.getAll('color_tag')
  const facetTags = urlParams.getAll('facet_tag')
  const priceMin = urlParams.get('price_min')
  const priceMax = urlParams.get('price_max')
  const page = Number(urlParams.get('page') || 1)
  const limit = Number(urlParams.get('limit') || 48)
  // URLに価格条件があるときは、集計結果で初期価格レンジを上書きしない。
  const hasPriceFilterFromUrl =
    urlParams.has('price_min') || urlParams.has('price_max')

  createApp({
    name: 'SnippetsProductsSearch',
    data() {
      return {
        showFilters: false,
        searchParameters: {
          collectionId,
          keyword,
          onlyInStock,
          productTypes,
          tagsSet: {
            tasteTags,
            colorTags,
            facetTags,
          },
          priceRange: {
            min: priceMin !== null ? Number(priceMin) : undefined,
            max: priceMax !== null ? Number(priceMax) : undefined,
          },
          sortBy,
          page: Number.isFinite(page) && page > 0 ? page : 1,
          limit: Number.isFinite(limit) && limit > 0 ? limit : 48,
        },
        products: [],
        totalCount: 0,
        totalPages: 1,
        aggregations: defaultAggregations,
        sortOptions: SORT_OPTIONS,
        isLoading: true,
        isZetaScriptSummaryListLoaded: !!window.zvRenderReviewSummary,
        hasPriceFilterFromUrl,
      }
    },
    components: { FProductCard, CategoryListContent, ProductsSearchFilters },
    mounted() {
      if (isDesktop()) {
        document.querySelector('.wrapper')?.classList.add(CLASS_STICKY_WRAPPER)
      }

      this.setInitialAggregations()
      this.searchProducts()

      // Zetaのスクリプトの読み込みが完了していない場合、読み込み完了を待ってから星をセットする
      if (!this.isZetaScriptSummaryListLoaded) {
        window.addEventListener(
          'zeta-script-summary-list-loaded',
          () => {
            this.isZetaScriptSummaryListLoaded = true
            this.setStar()
          },
          { once: true }
        )
      }
    },
    beforeUnmount() {
      document.querySelector('.wrapper')?.classList.remove(CLASS_STICKY_WRAPPER)
    },
    watch: {
      'searchParameters.sortBy'() {
        this.searchParameters.page = 1
        this.searchProducts()
      },
      'searchParameters.limit'() {
        this.searchParameters.page = 1
        this.searchProducts()
      },
      'searchParameters.page'() {
        this.searchProducts()
      },
    },
    computed: {
      showDiscountRate() {
        return this.searchParameters.sortBy === 'discount_rate_desc'
      },
      paginationItems() {
        const total = Number(this.totalPages) || 1
        const current = Number(this.searchParameters.page) || 1

        if (total <= 1) return [1]
        if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1)

        const items = [1]

        if (current <= 3) {
          items.push(2, 3, 4, 'ellipsis', total)
          return items
        }

        if (current >= total - 2) {
          items.push('ellipsis', total - 3, total - 2, total - 1, total)
          return items
        }

        items.push(
          'ellipsis',
          current - 1,
          current,
          current + 1,
          'ellipsis',
          total
        )
        return items
      },
      hasNextPage() {
        return this.searchParameters.page < this.totalPages
      },
      textNotFound() {
        if (this.isLoading) return ''
        return this.searchParameters.keyword && isCollectionPage
          ? `このカテゴリ内では「${this.searchParameters.keyword}」は見つかりませんでした。`
          : 'アイテムが見つかりませんでした。'
      },
      href() {
        return isCollectionPage && this.searchParameters.keyword
          ? `/search?q=${encodeURIComponent(this.searchParameters.keyword)}`
          : null
      },
    },
    methods: {
      updateUrlFromSearchParameters() {
        const url = new URL(window.location.href)
        const params = url.searchParams

        const setOrDelete = (key, value) => {
          if (value === undefined || value === null || value === '') {
            params.delete(key)
            return
          }
          params.set(key, String(value))
        }

        const setArray = (key, values) => {
          params.delete(key)
          if (!Array.isArray(values) || values.length === 0) return
          values.forEach((value) => {
            if (value !== undefined && value !== null && value !== '') {
              params.append(key, value)
            }
          })
        }

        setOrDelete('q', this.searchParameters.keyword)
        params.set(
          'sort_by',
          normalizeSortByValue(this.searchParameters.sortBy)
        )
        setOrDelete('in_stock', this.searchParameters.onlyInStock ? '1' : '')
        setArray('product_type', this.searchParameters.productTypes)
        setArray('taste_tag', this.searchParameters.tagsSet.tasteTags)
        setArray('color_tag', this.searchParameters.tagsSet.colorTags)
        setArray('facet_tag', this.searchParameters.tagsSet.facetTags)

        const isPriceFiltered =
          this.searchParameters.priceRange?.min !== undefined ||
          this.searchParameters.priceRange?.max !== undefined
        if (isPriceFiltered) {
          setOrDelete('price_min', this.searchParameters.priceRange.min)
          setOrDelete('price_max', this.searchParameters.priceRange.max)
        } else {
          params.delete('price_min')
          params.delete('price_max')
        }

        setOrDelete('page', this.searchParameters.page)
        setOrDelete('limit', this.searchParameters.limit)

        const nextUrl = `${url.pathname}${
          params.toString() ? `?${params.toString()}` : ''
        }${url.hash}`
        const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
        if (nextUrl !== currentUrl) {
          window.history.replaceState({}, '', nextUrl)
        }
      },

      async searchProducts() {
        this.isLoading = true
        const { products, totalCount, totalPages } = await searchProducts(
          this.searchParameters
        )
        this.products = products.map((product) => new OSProduct(product))
        this.totalCount = totalCount
        this.totalPages = totalPages

        // 検索条件を URL クエリパラメータに反映
        this.updateUrlFromSearchParameters()

        this.$nextTick(() => {
          this.setStar()
        })
        this.isLoading = false
        this.showFilters = false
      },

      async searchByFilters() {
        const shouldWaitForFiltersClose = this.showFilters
        this.searchParameters.page = 1
        await this.searchProducts()
        this.scrollToResultsSummary({
          shouldWaitForFiltersClose,
        })
      },

      async setInitialAggregations() {
        const { aggregations } = await searchProducts({
          collectionId: this.searchParameters.collectionId,
        })
        this.aggregations = aggregations
        // URLパラメーターで価格条件が指定されていない場合は、API から取得した最小/最大価格を初期値としてセット
        if (!this.hasPriceFilterFromUrl) {
          this.searchParameters.priceRange = aggregations.priceRange
        }
      },

      async reset() {
        this.searchParameters.keyword = ''
        this.searchParameters.onlyInStock = false
        this.searchParameters.page = 1
        this.searchParameters.productTypes = []
        this.searchParameters.tagsSet = {
          tasteTags: [],
          colorTags: [],
          facetTags: [],
        }
        this.searchParameters.priceRange = this.aggregations.priceRange
        await this.searchProducts()
      },

      async resetKeyword() {
        this.searchParameters.keyword = ''
        this.searchParameters.page = 1
        await this.searchProducts()
      },

      goToPage(page) {
        if (typeof page !== 'number') return
        if (page < 1 || page > this.totalPages) return
        if (this.searchParameters.page === page) return
        this.searchParameters.page = page
        this.scrollToResultsSummary()
      },

      goToNextPage() {
        if (!this.hasNextPage) return
        this.searchParameters.page += 1
        this.scrollToResultsSummary()
      },

      scrollToResultsSummary({ shouldWaitForFiltersClose = false } = {}) {
        const scroll = () => {
          const anchorElement = document.querySelector(
            `#${ID_PRODUCTS_SEARCH} .sn-products-search__main-content-header`
          )

          // Backward-compatible fallback for legacy markup.
          const summaryElement =
            anchorElement ||
            document.querySelector(
              `#${ID_PRODUCTS_SEARCH} .sn-products-search__summary`
            )
          if (!summaryElement) return

          const fixedOffset = 140
          const top =
            window.scrollY +
            summaryElement.getBoundingClientRect().top -
            fixedOffset
          window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
        }

        // SPの絞り込みモーダルが閉じた後、検索トップにスクロールする
        if (shouldWaitForFiltersClose) {
          window.setTimeout(() => {
            window.requestAnimationFrame(scroll)
          }, 340)
          return
        }

        window.requestAnimationFrame(scroll)
      },

      setStar() {
        if (!this.isZetaScriptSummaryListLoaded) return
        const elementsZvSummary = document.querySelectorAll(
          `#${ID_PRODUCTS_SEARCH} .zv-summary`
        )
        Array.from(elementsZvSummary).forEach(
          (element) => (element.innerHTML = '')
        )
        window.zvRenderReviewSummary(elementProductsSearch)
      },
    },
  }).mount(elementProductsSearch)
})

const SORT_OPTIONS = [
  { value: 'relevance', label: 'おすすめ順' },
  { value: 'price_asc', label: '価格の安い順' },
  { value: 'price_desc', label: '価格の高い順' },
  { value: 'published_at_desc', label: '新着順' },
  { value: 'discount_rate_desc', label: 'OFF率順' },
]

const SORT_BY_MAP = {
  relevance: 'relevance',
  price_desc: 'price_desc',
  price_asc: 'price_asc',
  published_at_desc: 'published_at_desc',
  discount_rate_desc: 'discount_rate_desc',
  'price-descending': 'price_desc',
  'price-ascending': 'price_asc',
  'mf-additional-published_at_time-descending': 'published_at_desc',
  'sale-descending': 'discount_rate_desc',
}

const LEGACY_PARAM_MAP = {
  gf_613923: { type: 'direct', nextKey: 'in_stock' },
  gf_613930: { type: 'direct', nextKey: 'in_stock' },
  gf_613925: { type: 'direct', nextKey: 'facet_tag' },
  gf_613932: { type: 'direct', nextKey: 'facet_tag' },
  gf_613926: { type: 'direct', nextKey: 'taste_tag' },
  gf_613933: { type: 'direct', nextKey: 'taste_tag' },
  gf_613927: { type: 'priceRange', minKey: 'price_min', maxKey: 'price_max' },
  gf_613934: { type: 'priceRange', minKey: 'price_min', maxKey: 'price_max' },
  gf_613928: { type: 'direct', nextKey: 'color_tag' },
  gf_613935: { type: 'direct', nextKey: 'color_tag' },
  gf_672936: {
    type: 'fixedArray',
    nextKey: 'product_type',
    fixedValues: ['廃番（家具）', '廃番（雑貨）'],
  },
  gf_672937: {
    type: 'fixedArray',
    nextKey: 'product_type',
    fixedValues: ['廃番（家具）', '廃番（雑貨）'],
  },
}

const normalizeSortByValue = (value) => SORT_BY_MAP[value] || 'relevance'
const normalizePriceParamValue = (value) => {
  if (value.trim() === '') return value
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return value
  return String(Math.trunc(numericValue))
}

// F&S 時代の古いパラメータでアクセスがあった際に検索条件を再現するための変換処理
const normalizeLegacyProductsSearchParams = (search) => {
  const currentParams = new URLSearchParams(search)
  const normalizedParams = new URLSearchParams()
  // 実際に値を書き換えたときだけtrueになり、上流でreplaceStateの実行判定に使う。
  let didNormalize = false

  currentParams.forEach((value, key) => {
    if (key === 'sort_by') {
      // 旧sort値を現行値へ寄せ、未定義値はrelevanceへフォールバックする。
      const normalizedSortBy = normalizeSortByValue(value)
      normalizedParams.append('sort_by', normalizedSortBy)
      if (normalizedSortBy !== value) didNormalize = true
      return
    }

    if (key === 'price_min' || key === 'price_max') {
      const normalizedPrice = normalizePriceParamValue(value)
      normalizedParams.append(key, normalizedPrice)
      if (normalizedPrice !== value) didNormalize = true
      return
    }

    const conversion = LEGACY_PARAM_MAP[key]

    if (!conversion) {
      normalizedParams.append(key, value)
      return
    }

    didNormalize = true

    if (conversion.type === 'direct') {
      normalizedParams.append(conversion.nextKey, value)
      return
    }

    if (conversion.type === 'fixedArray') {
      const existingValues = normalizedParams.getAll(conversion.nextKey)
      conversion.fixedValues.forEach((fixedValue) => {
        if (!existingValues.includes(fixedValue)) {
          normalizedParams.append(conversion.nextKey, fixedValue)
        }
      })
      return
    }

    // 価格は "min:max" の1値を分解して2パラメータへ展開する。
    const [min = '', max = ''] = value.split(':', 2)
    if (min !== '') {
      normalizedParams.append(conversion.minKey, normalizePriceParamValue(min))
    }
    if (max !== '') {
      normalizedParams.append(conversion.maxKey, normalizePriceParamValue(max))
    }
  })

  if (!didNormalize) {
    return { normalizedParams: currentParams, didNormalize: false }
  }

  return { normalizedParams, didNormalize: true }
}
